import { useCallback, useEffect, useState } from 'react';
import bugReportService, { SubmitBugReportPayload } from '../api/services/bugReportService';
import { ApiUrls } from '../api/ApiUrls';
import { isPluginBuild, isWebBuild } from '../utils/environment';
import { useTrackEvent } from '../analytics/hooks/useTrackEvent';

export function useReportProblem(options: { navigate: (to: string | number | string) => void; showToast: (opts: { message?: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void; getConversationId?: () => string | null; getPageUrl?: () => string | null; getProjectId?: () => string | null }) {
  const [issueType, setIssueType] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { trackEvent } = useTrackEvent();

  // Read stored source URL (saved when user clicked menu item)
  const getSourceUrl = useCallback(() => {
    try {
      // Prefer explicit previous-page marker when present (set by plugin navigation)
      const prev = sessionStorage.getItem('ideoz_report_previous_page');
      if (prev) return prev;
      return sessionStorage.getItem('ideoz_report_source_url');
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    trackEvent('ui.report_problem.opened');
  }, [trackEvent]);

  // Clear validation error when the user selects an issue type
  useEffect(() => {
    if (issueType && error) {
      setError(null);
    }
  }, [issueType, error]);

  const validate = useCallback(() => {
    if (!issueType) {
      setError('Please select an issue type');
      return false;
    }
    setError(null);
    return true;
  }, [issueType]);

  const submit = useCallback(async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    // Resolve a sensible pageUrl for the bug report. Prefer the stored source URL,
    // then `document.referrer` (useful inside embedded/plugin contexts), then
    // `window.location.href`. As a last resort leave null and let the server
    // validate; but prefer a platform link if available.
    let pageUrl: string | null = null;
    try {
      // Prefer stored session value (this represents the "previous" page
      // that navigation saved before opening the report UI). This is
      // especially important for plugin builds where `navigation.getPageUrl()`
      // returns the current page ("reportProblem").
      try {
        const stored = getSourceUrl();
        if (stored) pageUrl = stored;
      } catch (e) {}

      // Then allow an explicit platform-supplied page URL as a fallback.
      if (!pageUrl) {
        try {
          const provided = typeof options.getPageUrl === 'function' ? options.getPageUrl() : null;
          if (provided) pageUrl = provided;
        } catch (e) {
          // ignore
        }
      }

      // Fallbacks
      if (!pageUrl) {
        if (typeof document !== 'undefined' && document.referrer) pageUrl = document.referrer;
        else if (typeof window !== 'undefined' && window.location && window.location.href) pageUrl = window.location.href;
      }

      if (pageUrl === '') pageUrl = null;

      // If running inside plugin builds and the pageUrl is a plain page name
      // (e.g. "conversation"), keep it as-is; the server expects a short
      // identifier in plugin reports. Otherwise, ensure an absolute HTTP(s)
      // URL and fall back to platform link when invalid.
      const isPlainPageName = pageUrl && /^[a-zA-Z0-9_-]+$/.test(pageUrl);
      if (isPluginBuild() && isPlainPageName) {
        // keep pageUrl as the plain page name for plugin builds
      } else {
        if (pageUrl) {
          try {
            const resolved = new URL(pageUrl, typeof window !== 'undefined' ? window.location.href : undefined as any);
            if (!['http:', 'https:'].includes(resolved.protocol)) {
              console.warn('useReportProblem: pageUrl has non-http protocol, falling back to platformLink', pageUrl);
              pageUrl = ApiUrls.platformLink || null;
            } else {
              pageUrl = resolved.href;
            }
          } catch (e) {
            console.warn('useReportProblem: invalid pageUrl, falling back to platformLink', pageUrl);
            pageUrl = ApiUrls.platformLink || null;
          }
        } else {
          pageUrl = ApiUrls.platformLink || null;
        }
      }
    } catch (e) {
      pageUrl = ApiUrls.platformLink || null;
    }

    // Determine origin page (prefer explicit navigation param, then stored marker)
    let originPage: string | null = null;
    try {
      const provided = typeof options.getPageUrl === 'function' ? options.getPageUrl() : null;
      if (provided) originPage = provided;
    } catch (e) {
      originPage = null;
    }

    if (!originPage) {
      try {
        originPage = getSourceUrl();
      } catch (e) {
        originPage = null;
      }
    }

    // Resolve IDs only when origin page indicates they are relevant.
    // conversationId: only when origin is 'conversation'
    // projectId: only when origin is 'projectDetails'
    let conversationId: string | null = null;
    let projectId: string | null = null;

    try {
      if (originPage === 'conversation') {
        conversationId = typeof options.getConversationId === 'function' ? options.getConversationId() : null;
        // Try navigation-provided project id first (when available)
        if (typeof options.getProjectId === 'function') {
          try {
            const maybePid = options.getProjectId();
            if (maybePid) projectId = maybePid;
          } catch (e) {}
        }
        if (!conversationId) {
          try {
            const fromStorage = sessionStorage.getItem('ideoz_report_conversation_id');
            if (fromStorage) conversationId = fromStorage;
          } catch (e) {}
        }
      } else if (originPage === 'projectDetails') {
        // use the navigation-provided id when available; fall back to sessionStorage project id
        try {
          const maybe = typeof options.getConversationId === 'function' ? options.getConversationId() : null;
          if (maybe) projectId = maybe;
        } catch (e) {}
        if (!projectId) {
          try {
            const fromStorage = sessionStorage.getItem('ideoz_report_project_id');
            if (fromStorage) projectId = fromStorage;
          } catch (e) {}
        }
      }
    } catch (e) {}

    // Defensive fallback: if we resolved a conversationId (plugin context)
    // but did not yet obtain a projectId, try to read the project id from
    // sessionStorage. In the plugin the project id is sometimes persisted
    // separately by navigation or page mounts, so prefer that value when
    // present.
    try {
      if (isPluginBuild() && conversationId && !projectId) {
        const stored = sessionStorage.getItem('ideoz_report_project_id');
        if (stored) projectId = stored;
      }
    } catch (e) {}

    const payload: SubmitBugReportPayload = {
      issueType: issueType as any,
      description: description || null,
      conversationId: conversationId || null,
      projectId: projectId || null,
      pageUrl,
      sourceType: isPluginBuild() ? 'plugin' : isWebBuild() ? 'web' : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    try {
      // payload logged in analytics; avoid local debug output in production
      await bugReportService.submitBugReport(payload);
      trackEvent('ui.report_problem.submitted', { issueType });
      // Show success dialog (page handles navigation on dismiss)
      setIsSuccess(true);
    } catch (e) {
      console.error('Failed to submit bug report', e);
      options.showToast({ message: 'Something went wrong. Please try again.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, issueType, description, getSourceUrl, options, trackEvent]);

  const cancel = useCallback(() => {
    // Prefer explicit platform-supplied page name (e.g., from navigation params)
    let src: string | null = null;
    try {
      const provided = typeof options.getPageUrl === 'function' ? options.getPageUrl() : null;
      if (provided) src = provided;
    } catch (e) {}

    // Fallback to stored session value
    if (!src) {
      try { src = getSourceUrl(); } catch (e) { src = null; }
    }

    try { sessionStorage.removeItem('ideoz_report_source_url'); } catch (e) {}

    if (!src) {
      options.navigate(-1 as any);
      return;
    }

    // If plugin build and src is a plain page name, navigate using platform handler
    try {
      if (isPluginBuild() && src && /^[a-zA-Z0-9_-]+$/.test(src)) {
        options.navigate(src as any);
        return;
      }
    } catch (e) {}

    try {
      if (typeof window !== 'undefined') {
        const url = new URL(src, window.location.href);
        if (url.origin === window.location.origin) {
          const path = url.pathname + url.search + url.hash;
          options.navigate(path as any);
        } else {
          // external origin — perform full navigation
          window.location.href = src;
        }
      } else {
        options.navigate(src as any);
      }
    } catch (e) {
      options.navigate(src as any);
    }
  }, [getSourceUrl, options]);

  const dismissSuccess = useCallback(() => {
    // Prefer explicit platform-provided page name (navigation params)
    let src: string | null = null;
    try {
      const provided = typeof options.getPageUrl === 'function' ? options.getPageUrl() : null;
      if (provided) src = provided;
    } catch (e) {}

    if (!src) {
      try { src = getSourceUrl(); } catch (e) { src = null; }
    }

    try { sessionStorage.removeItem('ideoz_report_source_url'); } catch (e) {}
    setIsSuccess(false);

    if (!src) {
      options.navigate(-1 as any);
      return;
    }

    // If plugin build and src is a plain page name, navigate using platform handler
    try {
      if (isPluginBuild() && src && /^[a-zA-Z0-9_-]+$/.test(src)) {
        options.navigate(src as any);
        return;
      }
    } catch (e) {}

    try {
      if (typeof window !== 'undefined') {
        const url = new URL(src, window.location.href);
        if (url.origin === window.location.origin) {
          const path = url.pathname + url.search + url.hash;
          options.navigate(path as any);
        } else {
          window.location.href = src;
        }
      } else {
        options.navigate(src as any);
      }
    } catch (e) {
      options.navigate(src as any);
    }
  }, [getSourceUrl, options]);

  return {
    issueType,
    setIssueType,
    description,
    setDescription,
    isSubmitting,
    error,
    submit,
    cancel,
    isSuccess,
    dismissSuccess,
  };
}

export default useReportProblem;
